import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { TimePeriod } from './driver-score-utils';

// Re-export TimePeriod for backwards compatibility
export type { TimePeriod } from './driver-score-utils';

/**
 * Props for the DriverScoreTabs component.
 */
interface DriverScoreTabsProps {
	/** Currently selected time period. */
	value: TimePeriod;
	/** Callback fired when the selected time period changes. */
	onChange: (newPeriod: TimePeriod) => void;
}

/**
 * Time period toggle buttons for filtering driver scores.
 *
 * Provides three options for viewing driver scores: overall (all time),
 * this week, or this month. Uses MUI ToggleButtonGroup with pill-style
 * buttons and primary color highlighting for the selected option.
 *
 * @component
 * @example
 * ```tsx
 * const [period, setPeriod] = useState<TimePeriod>('overall');
 *
 * <DriverScoreTabs
 *   value={period}
 *   onChange={(newPeriod) => setPeriod(newPeriod)}
 * />
 * ```
 */
const DriverScoreTabs: React.FC<DriverScoreTabsProps> = ({ value, onChange }) => {
	const theme = useTheme();

	const handleChange = (_event: React.MouseEvent<HTMLElement>, newPeriod: TimePeriod | null) => {
		if (newPeriod !== null) {
			onChange(newPeriod);
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				mb: 3,
				backgroundColor: theme.palette.grey[100],
				borderRadius: '5px',
				padding: 0.5,
				width: 'fit-content',
				marginX: 'auto',
			}}
		>
			<ToggleButtonGroup
				value={value}
				exclusive
				onChange={handleChange}
				size='small'
				sx={{
					gap: 0.5,
					'& .MuiToggleButton-root': {
						textTransform: 'none',
						px: 2,
						py: 0.65,
						fontSize: '0.875rem',
						border: 'none',
						borderRadius: '5px !important',
						backgroundColor: theme.palette.grey[100],
						color: theme.palette.text.secondary,
						'&.Mui-selected': {
							backgroundColor: theme.palette.primary.main,
							color: '#FFFFFF',
							'&:hover': {
								backgroundColor: theme.palette.primary.dark,
							},
						},
						'&:not(.Mui-selected)': {
							backgroundColor: 'transparent',
							'&:hover': {
								backgroundColor: theme.palette.action.hover,
							},
						},
					},
				}}
			>
				<ToggleButton value='overall'>Overall score</ToggleButton>
				<ToggleButton value='week'>This week&apos;s score</ToggleButton>
				<ToggleButton value='month'>This month&apos;s score</ToggleButton>
			</ToggleButtonGroup>
		</Box>
	);
};

export default DriverScoreTabs;
